import { Box, Stack, Tooltip, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import { SEMANTIC_COLOR_VARS, type SemanticColor } from '../../../shared/components/semanticColor';
import { formatScore, scoreBand, type ScoreBand } from '../scoring';

export { formatScore, scoreBand, type ScoreBand };

export const BAND_COLOR: Record<ScoreBand, SemanticColor> = { strong: 'success', mixed: 'warning', weak: 'error' };
export const BAND_LABEL: Record<ScoreBand, string> = { strong: 'Strong', mixed: 'Mixed', weak: 'Weak' };

/**
 * A ring meter for a 1–10 score: the filled arc wears the band's color, the
 * track a lighter step of the same color, the value sits in the middle.
 */
export function ScoreRing({ score, size = 112, label }: { score: number; size?: number; label?: string }) {
  const vars = SEMANTIC_COLOR_VARS[BAND_COLOR[scoreBand(score)]];
  const stroke = 9;
  const r = (size - stroke) / 2;
  const circumference = 2 * Math.PI * r;
  const filled = (Math.min(10, Math.max(0, score)) / 10) * circumference;
  return (
    <Box sx={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label={`Score ${formatScore(score)} of 10`}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={vars.soft} strokeWidth={stroke} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={vars.main}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={`${filled} ${circumference - filled}`}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography component="span" sx={{ fontSize: size * 0.3, fontWeight: 800, lineHeight: 1, letterSpacing: '-0.02em' }}>
          {formatScore(score)}
        </Typography>
        <Typography component="span" variant="caption" color="text.secondary" sx={{ mt: 0.25 }}>
          {label ?? 'of 10'}
        </Typography>
      </Box>
    </Box>
  );
}

interface BarProps {
  /** 0–1 share of the track. */
  fraction: number;
  color: SemanticColor;
  /** A hairline reference mark at this share of the track, e.g. a benchmark. */
  marker?: number;
  height?: number;
}

/** A thin horizontal bar: square at the baseline, rounded at the data end, on a recessive track. */
export function Bar({ fraction, color, marker, height = 10 }: BarProps) {
  const vars = SEMANTIC_COLOR_VARS[color];
  const width = `${Math.min(100, Math.max(0, fraction * 100))}%`;
  return (
    <Box
      sx={{
        position: 'relative',
        height,
        borderRadius: 'var(--radius-full)',
        bgcolor: 'action.hover',
        overflow: 'hidden',
        width: '100%',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width,
          bgcolor: vars.main,
          borderRadius: '0 4px 4px 0',
          transition: 'width var(--transition-slow)',
        }}
      />
      {marker != null && (
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            top: -2,
            bottom: -2,
            left: `${marker * 100}%`,
            width: 2,
            bgcolor: 'text.secondary',
            opacity: 0.7,
          }}
        />
      )}
    </Box>
  );
}

interface LabeledBarProps {
  label: ReactNode;
  valueLabel: string;
  fraction: number;
  color: SemanticColor;
  marker?: number;
  tooltip?: string;
  /** Width of the label column; the bar takes the rest. */
  labelWidth?: number | string;
}

/** One row of a horizontal bar chart: label, bar, value at the tip. */
export function LabeledBar({ label, valueLabel, fraction, color, marker, tooltip, labelWidth = 180 }: LabeledBarProps) {
  const row = (
    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0, py: 0.25 }}>
      <Typography
        variant="body2"
        sx={{
          width: labelWidth,
          flexShrink: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </Typography>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Bar fraction={fraction} color={color} marker={marker} />
      </Box>
      <Typography
        variant="body2"
        fontWeight={700}
        sx={{ width: 48, flexShrink: 0, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}
      >
        {valueLabel}
      </Typography>
    </Stack>
  );
  return tooltip ? (
    <Tooltip title={tooltip} placement="top-start">
      {row}
    </Tooltip>
  ) : (
    row
  );
}

export interface Segment {
  label: string;
  value: number;
  color: SemanticColor;
  icon?: ReactNode;
}

/**
 * A single part-to-whole bar with a 2px surface gap between segments, and a
 * legend row underneath that carries each segment's label, count and (for
 * status colors) its icon, so no segment relies on color alone.
 */
export function SegmentBar({ segments, height = 12, unit }: { segments: Segment[]; height?: number; unit?: string }) {
  const total = segments.reduce((sum, s) => sum + s.value, 0) || 1;
  return (
    <Box>
      <Stack direction="row" spacing="2px" sx={{ height, borderRadius: 'var(--radius-full)', overflow: 'hidden' }}>
        {segments
          .filter((s) => s.value > 0)
          .map((s) => (
            <Tooltip key={s.label} title={`${s.label}: ${s.value}${unit ?? ''}`}>
              <Box sx={{ flex: s.value / total, bgcolor: SEMANTIC_COLOR_VARS[s.color].main, minWidth: 4 }} />
            </Tooltip>
          ))}
      </Stack>
      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
        {segments.map((s) => (
          <Stack key={s.label} direction="row" spacing={0.75} alignItems="center">
            <Box
              component="span"
              sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: SEMANTIC_COLOR_VARS[s.color].main, flexShrink: 0 }}
            />
            {s.icon}
            <Typography variant="caption" color="text.secondary">
              <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
                {s.value}
                {unit}
              </Box>{' '}
              {s.label}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
}

/** A headline number with its label above and one line of context below. */
export function StatTile({
  label,
  value,
  detail,
  footer,
}: {
  label: string;
  value: string;
  detail?: string;
  footer?: ReactNode;
}) {
  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 'var(--radius-md)',
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
        minWidth: 0,
      }}
    >
      <Typography variant="caption" color="text.secondary" fontWeight={600}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 26, fontWeight: 800, lineHeight: 1.15, letterSpacing: '-0.02em' }}>{value}</Typography>
      {detail && (
        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.4 }}>
          {detail}
        </Typography>
      )}
      {footer && <Box sx={{ mt: 'auto', pt: 0.5 }}>{footer}</Box>}
    </Box>
  );
}
